// SOLS Foundation's impact figures as this site renders them.
//
// The CMS `impactStats` node is the source: services/cms/impact.ts lays the
// published rows over this object at render time, and a failed read lays the
// generated snapshot of those rows (constants/generated/impact_stats.ts)
// instead. The figures below are the last resort, used only for a key neither
// supplies, and are never hardcoded in copy or locale JSON: locale JSON carries
// a {{placeholder}} that the rendering component fills via stat() or statNum().
//
// The labels are this site's own wording and are not taken from CMS rows, so a
// figure edited for another site cannot reword this one.
export type ImpactStat = {
  value: number;
  prefix?: string;
  suffix?: string;
  label?: string;
};

/** CMS key of SOLS Foundation's founding date: a date row, not a figure. */
export const FOUNDATION_FOUNDING_DATE_KEY = "foundation_founding_date";

/** CMS key of SOLS Solar Academy's founding date: a date row, not a figure. */
export const SOLAR_ACADEMY_FOUNDING_DATE_KEY = "solar_academy_founding_date";

// The founding days behind the two year counts, used only when neither the
// live read nor the snapshot holds a published date row for them. Both match
// the CMS rows. next-i18next.config.js reads the same rows from the snapshot's
// JSON twin and falls back to the same days.
export const FOUNDING = {
  foundation: new Date("2000-01-10"),
  solarAcademy: new Date("2014-11-01"),
} as const;

// Completed whole years from `date` to `now`, date-aware: the current year only
// counts once the anniversary has passed.
export const yearsSince = (date: Date, now: Date = new Date()): number => {
  let years = now.getFullYear() - date.getFullYear();
  const anniversaryPassed =
    now.getMonth() > date.getMonth() ||
    (now.getMonth() === date.getMonth() && now.getDate() >= date.getDate());
  if (!anniversaryPassed) years -= 1;
  return years;
};

export const impactStats = {
  // Off-grid Orang Asli & Orang Asal homes powered. The same CMS row feeds
  // SOLS Energy's figure, so both sites show one number.
  energy_access_homes: {
    value: 5060,
    suffix: "+",
    label: "Orang Asli (Indigenous) homes powered",
  },

  // Cumulative lives transformed across all programmes since 2000.
  lives_transformed: {
    value: 800000,
    suffix: "+",
    label: "lives transformed",
  },

  // Ages, not stored figures: services/cms/impact.ts ages the founding date
  // rows, and these fallbacks age FOUNDING the same way, so both tick over on
  // the anniversary without a manual bump. The key names stay as they are
  // because data/impactProgrammes/*.json refers to them as a `statKey`.
  years_of_service: {
    value: yearsSince(FOUNDING.foundation),
    label: "years of service",
  },
  solar_academy_years_running: {
    value: yearsSince(FOUNDING.solarAcademy),
    label: "years running",
  },

  // --- SOLS Solar Academy (solar vocational training) ---
  // `solar_academy_` keyword keeps it distinct from the other academies
  // (SOLS ASLI Women Academy / SAWA, TVET Academy). The graduate figure also
  // shows on SOLS Energy, from the same CMS row.
  solar_academy_graduates: {
    value: 870,
    suffix: "+",
    label: "SOLS Solar Academy graduates",
  },
  solar_academy_employment_rate: {
    value: 91,
    suffix: "%",
    label: "graduates employed within their first month",
  },
  solar_academy_income_increase: {
    value: 83,
    suffix: "%",
    label: "average graduate income increase",
  },

  // --- Skills Hub (free online learning platform) ---
  // `skills_hub_` keyword: Foundation runs many education programmes, so key by
  // programme, not by "foundation".
  skills_hub_learners: {
    value: 37369,
    label: "Skills Hub learners",
  },
  skills_hub_classes: {
    value: 3171,
    label: "online classes conducted",
  },
  skills_hub_hours: {
    value: 4757,
    label: "hours of learning",
  },

  // --- Energy Access / Kampung Solar programme ---
  // The eight figures the energy-access page renders as a tile grid. The grid's
  // "Rural homes" tile is the same metric as `energy_access_homes` above, so it
  // reuses that key rather than duplicating it.
  //
  // Unit suffixes are stored bare ("MWh", not " MWh") to match the CMS rows.
  // stat() inserts the space for word units; see isTight below.
  energy_access_ngo_centres: {
    value: 17,
    label: "NGO / welfare centres powered",
  },
  energy_access_generation_mwh: {
    value: 485.93,
    suffix: "MWh",
    label: "clean energy generated to date",
  },
  energy_access_productivity_hours: {
    value: 23603190,
    suffix: "+",
    label: "productivity hours gained",
  },
  energy_access_rm_savings: {
    prefix: "RM",
    value: 47479969,
    label: "customer savings to date",
  },
  energy_access_co2_avoided: {
    value: 8368.68,
    suffix: "tCO2",
    label: "carbon emissions avoided to date",
  },
  energy_access_trees_planted: {
    value: 139478,
    label: "trees-equivalent planted",
  },
  energy_access_barrels_avoided: {
    value: 19462,
    label: "barrels of oil avoided",
  },
} satisfies Record<string, ImpactStat>;

export type ImpactStatKey = keyof typeof impactStats;

// Resolved map shape returned by services/cms/impact.ts (the CMS rows laid
// over this object). Both accessors below take one as an optional 3rd arg so
// pages and components can pass the resolved map; callers that don't pass one
// read the static object above.
export type ImpactStatsMap = typeof impactStats;

/**
 * Raw record shape stored in the CMS `impactStats` collection. A date row
 * carries its day in `dateTs` (epoch ms) rather than `value`; a range row
 * carries `low` and `high` and no `value`.
 */
export interface ImpactStatRecord {
  value?: number;
  valueType?: "number" | "date" | "range";
  low?: number;
  high?: number;
  dateTs?: number | null;
  /** When the owner last confirmed the figure; carried through, never shown. */
  confirmedAt?: number;
  prefix?: string | null;
  suffix?: string | null;
  label?: string;
  status?: string;
}

/**
 * The figure a row stands for, or undefined when this site cannot render it
 * as one number: a range, an unknown type, a non-finite value, or a date with
 * no usable timestamp. A date row stands for its age in whole years.
 */
export function figureOf(rec: ImpactStatRecord): number | undefined {
  const finite = (n: unknown): n is number =>
    typeof n === "number" && Number.isFinite(n);
  if (rec.valueType === "date") {
    // An invalid Date catches a finite timestamp beyond the range a Date can
    // hold, which would otherwise age to NaN years.
    const day = finite(rec.dateTs) ? new Date(rec.dateTs) : undefined;
    return day && !Number.isNaN(day.getTime()) ? yearsSince(day) : undefined;
  }
  if (rec.valueType === undefined || rec.valueType === "number") {
    return finite(rec.value) ? rec.value : undefined;
  }
  return undefined;
}

// Locale-aware bare figure, e.g. statNum("lives_transformed", "id") => "800.000".
// Use inside prose that already carries its own unit word ("more than {{lives}}
// lives", "{{rate}} per cent"), so the locale keeps its own wording.
export const statNum = (
  key: ImpactStatKey,
  locale: string = "en",
  stats: ImpactStatsMap = impactStats,
): string =>
  stats[key].value.toLocaleString(locale === "id" ? "id-ID" : "en-US");

// Which suffixes bind directly to the figure and which take a space before it.
// The same rule as every other SOLS site carrying this file, so the same
// figure renders the same way everywhere.
//
// An allowlist rather than a "does it start with a letter" test, because "K" is
// a letter and still binds tight ("695K"), while "MW", "MWh", "tCO2" and
// "million" all need the space.
//
// The space cannot live in the stored suffix instead: the CMS rows hold "MWh"
// and "tCO2" unpadded, and asking an editor to type a leading space into a
// text field would make correct output depend on invisible whitespace
// surviving every future edit.
const isTight = (suffix: string): boolean =>
  suffix === "" || suffix === "+" || suffix === "K" || suffix === "%";

// Locale-aware formatted figure with prefix/suffix, e.g.
// stat("energy_access_homes", "id") => "5.060+". Use for standalone stat
// chips and headline figures.
export const stat = (
  key: ImpactStatKey,
  locale: string = "en",
  stats: ImpactStatsMap = impactStats,
): string => {
  const s: ImpactStat = stats[key];
  const suffix = s.suffix ?? "";
  const sep = isTight(suffix) ? "" : " ";
  return `${s.prefix ?? ""}${statNum(key, locale, stats)}${sep}${suffix}`;
};
